import {
  createEventTicket,
  deactivateEventTicket,
  updateEventTicket,
  type AdminTicketType,
  type TicketTypeInput,
} from "../api/tickets";
import {
  MEMBERSHIP_TIER_LABELS,
  type EventPricingConfig,
  type MembershipTier,
} from "../types/pricingConfig";

const FULL_CONFERENCE_CATALOG: Record<
  MembershipTier,
  { itemOrderedLabel: string; description: string }
> = {
  ieee_student_life_retired: {
    itemOrderedLabel: "Full Conference Registration",
    description:
      "Full conference access for IEEE Student, Life, or Retired members. Price depends on active registration window.",
  },
  ieee_member: {
    itemOrderedLabel: "Full Conference Registration",
    description:
      "Full conference access for IEEE members. Price depends on active registration window.",
  },
  non_ieee_member: {
    itemOrderedLabel: "Full Conference Registration",
    description:
      "Full conference access for non-IEEE members. Price depends on active registration window.",
  },
};

const SINGLE_DAY_CATALOG: Record<
  MembershipTier,
  { itemOrderedLabel: string; description: string }
> = {
  ieee_student_life_retired: {
    itemOrderedLabel: "Limited Conference Registration: Day #",
    description:
      "Listener registration for IEEE Student, Life, or Retired members.",
  },
  ieee_member: {
    itemOrderedLabel: "Limited Conference Registration: Day #",
    description: "Listener registration for IEEE members.",
  },
  non_ieee_member: {
    itemOrderedLabel: "Limited Conference Registration: Day #",
    description: "Listener registration for non-IEEE members.",
  },
};

const BANQUET_CATALOG = {
  name: "Banquet Ticket",
  itemOrderedLabel: "Additional Dinner",
  description: "Banquet dinner ticket (not included in conference registration).",
};

const EXTRA_PAGE_CATALOG = {
  name: "Extra Page Fee",
  itemOrderedLabel: "Extra Page Charge",
  description: "Per-page fee for papers over 6 pages.",
};

const ADDITIONAL_PAPER_CATALOG = {
  name: "Additional Paper Registration",
  itemOrderedLabel: "Additional Paper Registration",
  description:
    "Flat-rate additional paper registration for in-person full conference authors ($380 USD each).",
  priceUsd: 380,
};

function usdToPence(usd: number): number {
  return Math.round(usd * 100);
}

function findCatalogTicket(
  tickets: AdminTicketType[],
  category: string,
  membershipTier: MembershipTier | null,
): AdminTicketType | undefined {
  return tickets.find(
    (t) =>
      t.category === category &&
      t.membershipTier === membershipTier &&
      t.status === "active",
  );
}

async function upsertCatalogTicket(
  eventId: number,
  existing: AdminTicketType | undefined,
  input: TicketTypeInput,
): Promise<AdminTicketType> {
  if (existing) {
    return updateEventTicket(existing.id, input);
  }
  return createEventTicket(eventId, input);
}

export interface ConferenceCatalogPrices {
  singleDayUsd: Record<MembershipTier, number>;
  banquetUsd: number | null;
  extraPageUsd: number;
}

export async function syncConferenceCatalog(
  eventId: number,
  pricingConfig: EventPricingConfig,
  catalogPrices: ConferenceCatalogPrices,
  existingTickets: AdminTicketType[],
): Promise<void> {
  const activeTickets = existingTickets.filter((t) => t.status === "active");

  for (const tier of Object.keys(MEMBERSHIP_TIER_LABELS) as MembershipTier[]) {
    const meta = FULL_CONFERENCE_CATALOG[tier];
    const existing = findCatalogTicket(activeTickets, "full_conference", tier);
    const earlyBirdUsd = pricingConfig.fullConferenceRatesUsd[tier].earlyBird;

    await upsertCatalogTicket(eventId, existing, {
      name: `Full Conference — ${MEMBERSHIP_TIER_LABELS[tier]}`,
      description: meta.description,
      pricePence: usdToPence(earlyBirdUsd),
      currency: "usd",
      category: "full_conference",
      membershipTier: tier,
      itemOrderedLabel: meta.itemOrderedLabel,
      status: "active",
    });
  }

  for (const tier of Object.keys(MEMBERSHIP_TIER_LABELS) as MembershipTier[]) {
    const meta = SINGLE_DAY_CATALOG[tier];
    const existing = findCatalogTicket(activeTickets, "single_day", tier);
    const usd = catalogPrices.singleDayUsd[tier];

    if (usd <= 0) {
      if (existing) await deactivateEventTicket(existing.id);
      continue;
    }

    await upsertCatalogTicket(eventId, existing, {
      name: `Listener — ${MEMBERSHIP_TIER_LABELS[tier]}`,
      description: meta.description,
      pricePence: usdToPence(usd),
      currency: "usd",
      category: "single_day",
      membershipTier: tier,
      itemOrderedLabel: meta.itemOrderedLabel,
      status: "active",
    });
  }

  const banquetExisting = activeTickets.find((t) => t.category === "add_on");
  if (catalogPrices.banquetUsd && catalogPrices.banquetUsd > 0) {
    await upsertCatalogTicket(eventId, banquetExisting, {
      name: BANQUET_CATALOG.name,
      description: BANQUET_CATALOG.description,
      pricePence: usdToPence(catalogPrices.banquetUsd),
      currency: "usd",
      category: "add_on",
      membershipTier: null,
      itemOrderedLabel: BANQUET_CATALOG.itemOrderedLabel,
      status: "active",
    });
  } else if (banquetExisting) {
    await deactivateEventTicket(banquetExisting.id);
  }

  const extraPageExisting = activeTickets.find((t) => t.category === "extra_page");
  const extraPageUsd = catalogPrices.extraPageUsd > 0 ? catalogPrices.extraPageUsd : 90;
  await upsertCatalogTicket(eventId, extraPageExisting, {
    name: EXTRA_PAGE_CATALOG.name,
    description: EXTRA_PAGE_CATALOG.description,
    pricePence: usdToPence(extraPageUsd),
    currency: "usd",
    category: "extra_page",
    membershipTier: null,
    itemOrderedLabel: EXTRA_PAGE_CATALOG.itemOrderedLabel,
    status: "active",
  });

  const additionalPaperExisting = activeTickets.find(
    (t) => t.category === "additional_paper",
  );
  await upsertCatalogTicket(eventId, additionalPaperExisting, {
    name: ADDITIONAL_PAPER_CATALOG.name,
    description: ADDITIONAL_PAPER_CATALOG.description,
    pricePence: usdToPence(ADDITIONAL_PAPER_CATALOG.priceUsd),
    currency: "usd",
    category: "additional_paper",
    membershipTier: null,
    itemOrderedLabel: ADDITIONAL_PAPER_CATALOG.itemOrderedLabel,
    status: "active",
  });
}

const CATALOG_CATEGORIES = [
  "full_conference",
  "single_day",
  "add_on",
  "extra_page",
  "additional_paper",
] as const;

export async function deactivateConferenceCatalog(
  existingTickets: AdminTicketType[],
): Promise<void> {
  for (const ticket of existingTickets) {
    if (
      ticket.status === "active" &&
      ticket.category &&
      CATALOG_CATEGORIES.includes(
        ticket.category as (typeof CATALOG_CATEGORIES)[number],
      )
    ) {
      await deactivateEventTicket(ticket.id);
    }
  }
}

export function extractConferenceCatalogPrices(
  tickets: AdminTicketType[],
): ConferenceCatalogPrices {
  const singleDayUsd = {
    ieee_student_life_retired: 0,
    ieee_member: 0,
    non_ieee_member: 0,
  } satisfies Record<MembershipTier, number>;

  for (const tier of Object.keys(singleDayUsd) as MembershipTier[]) {
    const row = tickets.find(
      (t) => t.category === "single_day" && t.membershipTier === tier,
    );
    if (row) singleDayUsd[tier] = row.pricePence / 100;
  }

  const banquet = tickets.find((t) => t.category === "add_on");
  const extraPage = tickets.find((t) => t.category === "extra_page");
  return {
    singleDayUsd,
    banquetUsd: banquet ? banquet.pricePence / 100 : null,
    extraPageUsd: extraPage ? extraPage.pricePence / 100 : 90,
  };
}
